import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router';
import {
  Button,
  Chip,
  DataGrid,
  Icon,
  navItemClass,
  PageHeader,
  SelectField,
  SidebarBrand,
  TextField,
  useToast,
  QrCode,
} from '@syrova/ui';
import { isPerpetualLicense } from '@syrova/contracts';
import { ApiError, api } from './client.ts';
import { useI18n } from './i18n.tsx';

interface Owner {
  id: string;
  email: string;
  displayName: string;
  role: string;
  tenantId: string;
  mfaEnrolled: boolean;
}
function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<Owner | null> => {
      try {
        return await api.get<Owner>('/portal/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
  });
}
const fmt = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('sv-SE', { hour12: false }).slice(0, 16) : '—'; // 2026-10-11 22:12
const STAGE_TONE: Record<string, string> = {
  none: 'neutral',
  ok: 'success',
  pre_expiry: 'warning',
  warning: 'warning',
  degraded: 'danger',
  floor: 'danger',
  not_yet_valid: 'warning',
};

function Login() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const login = useMutation({
    mutationFn: () =>
      api.post<Owner>('/portal/auth/login', { email, password, ...(code ? { code } : {}) }),
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });
  const needsCode =
    login.error instanceof ApiError &&
    (login.error.code === 'MFA_REQUIRED' || login.error.code === 'MFA_CODE_INVALID');
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-4">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          login.mutate();
        }}
        className="w-full max-w-sm rounded-lg border border-border bg-surface p-6"
        aria-labelledby="login-title"
      >
        <h1 id="login-title" className="mb-4 text-xl font-semibold">
          {t('cloud.portalName')}
        </h1>
        <div className="flex flex-col gap-4">
          <TextField
            label={t('cloud.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
            ltr
            autoComplete="username"
          />
          <TextField
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            ltr
            autoComplete="current-password"
            {...(login.error
              ? {
                  error:
                    login.error instanceof ApiError && login.error.code === 'INVALID_CREDENTIALS'
                      ? t('auth.invalidCredentials')
                      : t('errors.generic'),
                }
              : {})}
          />
          {needsCode ? (
            <TextField
              label={t('cloud.mfa.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              ltr
              hint={t('cloud.mfa.required')}
            />
          ) : null}
          <Button type="submit" loading={login.isPending} className="w-full">
            {t('auth.login')}
          </Button>
        </div>
      </form>
    </main>
  );
}

interface PropertyCard {
  id: string;
  code: string;
  nameAr: string;
  roomCap: number;
  node: {
    status: string;
    version: string | null;
    lastSeenAt: string | null;
    lastSeenMinutes: number | null;
  } | null;
  license: { stage: string; daysLeft: number | null; expiresAt: string | null };
  businessDate: string | null;
  summary: {
    occupancyPct: number | null;
    inHouse: number;
    arrivalsToday: number;
    departuresToday: number;
  } | null;
  backupLastOkAt: string | null;
  backupLastDrillOk: boolean | null;
  alerts: { code: string; level: string }[];
}
function Properties() {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<PropertyCard[]>('/portal/properties'),
    refetchInterval: 60_000,
  });
  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={t('cloud.portal.properties')} />
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(q.data ?? []).map((p) => {
          const m = p.node?.lastSeenMinutes;
          const conn =
            m === null || m === undefined
              ? { tone: 'danger', text: t('cloud.portal.offline') }
              : m < 10
                ? { tone: 'success', text: t('cloud.portal.connected') }
                : { tone: 'warning', text: t('cloud.portal.stale', { n: m }) };
          return (
            <section
              key={p.id}
              className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
              aria-label={p.nameAr}
            >
              <h2 className="font-semibold">
                {p.nameAr} <span className="num text-xs text-text-muted">{p.code}</span>
              </h2>
              <p className="flex flex-wrap gap-2 text-sm">
                <Chip tone={conn.tone}>{conn.text}</Chip>
                <Chip tone={STAGE_TONE[p.license.stage]}>
                  {t(`cloud.stages.${p.license.stage}` as 'cloud.stages.ok')}
                </Chip>
              </p>
              {p.license.expiresAt ? (
                <p className="num text-xs text-text-muted">
                  {t('license.expiresAt')}:{' '}
                  {isPerpetualLicense(p.license.expiresAt)
                    ? t('license.perpetual')
                    : fmt(p.license.expiresAt)}
                </p>
              ) : null}
              <p className="num text-xs text-text-muted">
                {t('cloud.portal.businessDate')}: {p.businessDate ?? '—'}
              </p>
              {p.summary ? (
                <dl className="grid grid-cols-2 gap-1 text-sm">
                  <dt className="text-text-muted">{t('cloud.portal.occupancy')}</dt>
                  <dd className="num">{p.summary.occupancyPct ?? '—'}%</dd>
                  <dt className="text-text-muted">{t('cloud.portal.inHouse')}</dt>
                  <dd className="num">{p.summary.inHouse}</dd>
                  <dt className="text-text-muted">{t('cloud.portal.arrivals')}</dt>
                  <dd className="num">{p.summary.arrivalsToday}</dd>
                  <dt className="text-text-muted">{t('cloud.portal.departures')}</dt>
                  <dd className="num">{p.summary.departuresToday}</dd>
                </dl>
              ) : (
                <p className="text-xs text-text-muted">{t('cloud.portal.noData')}</p>
              )}
              <p className="num text-xs text-text-muted">
                {t('cloud.portal.backup')}: {fmt(p.backupLastOkAt)}{' '}
                {p.backupLastDrillOk === null
                  ? ''
                  : p.backupLastDrillOk
                    ? `· ${t('cloud.portal.drillOk')}`
                    : `· ${t('cloud.portal.drillFailed')}`}
              </p>
              {p.alerts.filter((a) => a.level !== 'info').length ? (
                <p className="flex flex-wrap gap-1">
                  {p.alerts
                    .filter((a) => a.level !== 'info')
                    .map((a) => (
                      <Chip key={a.code} tone={a.level === 'critical' ? 'danger' : 'warning'}>
                        {a.code}
                      </Chip>
                    ))}
                </p>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

interface GrantRow {
  id: string;
  propertyCode: string;
  propertyNameAr: string;
  scope: string;
  reason: string;
  ticket: string;
  durationMinutes: number;
  status: string;
  createdAt: string;
  snapshotCount: number;
  lastActivityAt: string | null;
}
function Grants({ me }: { me: Owner }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['grants'],
    queryFn: () => api.get<GrantRow[]>('/portal/support-grants'),
    refetchInterval: 30_000,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) =>
      api.post(`/portal/support-grants/${id}/decision`, { decision }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['grants'] });
      toast.toast('success', t('cloud.grants.decided'));
    },
    onError: (e) =>
      toast.toast('error', e instanceof ApiError ? `${e.code}: ${e.message}` : t('errors.generic')),
  });
  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={t('cloud.grants.title')} />
      <p className="text-sm text-text-muted">{t('cloud.grants.portalHint')}</p>
      <DataGrid<GrantRow>
        rows={q.data ?? []}
        getRowId={(r) => r.id}
        emptyText={t('common.noResults')}
        caption={t('cloud.grants.title')}
        columns={[
          {
            key: 'when',
            header: t('health.cols.when'),
            ltr: true,
            render: (r) => fmt(r.createdAt),
          },
          {
            key: 'prop',
            header: t('cloud.grants.property'),
            render: (r) => `${r.propertyCode} · ${r.propertyNameAr}`,
          },
          {
            key: 'scope',
            header: t('cloud.grants.scope'),
            render: (r) => t(`cloud.grants.scopes.${r.scope}` as 'cloud.grants.scopes.full'),
          },
          {
            key: 'reason',
            header: t('cloud.grants.reason'),
            render: (r) => `${r.reason} (${r.ticket}, ${r.durationMinutes}m)`,
          },
          {
            key: 'snap',
            header: t('cloud.grants.snapshots'),
            ltr: true,
            render: (r) =>
              r.snapshotCount ? (
                <Button size="sm" variant="ghost" onClick={() => setSelected(r.id)}>
                  {r.snapshotCount} · {t('cloud.grants.view')}
                </Button>
              ) : (
                '—'
              ),
          },
          {
            key: 'st',
            header: t('cloud.grants.status'),
            render: (r) => (
              <Chip
                tone={
                  r.status === 'approved' || r.status === 'active'
                    ? 'success'
                    : r.status === 'requested'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {t(`cloud.grants.statuses.${r.status}` as 'cloud.grants.statuses.requested')}
              </Chip>
            ),
          },
          {
            key: 'act',
            header: t('common.actions'),
            render: (r) =>
              r.status === 'requested' && me.role === 'tenant_admin' ? (
                <span className="flex gap-1">
                  <Button
                    size="sm"
                    onClick={() => decide.mutate({ id: r.id, decision: 'approve' })}
                  >
                    {t('cloud.grants.approve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => decide.mutate({ id: r.id, decision: 'reject' })}
                  >
                    {t('cloud.grants.reject')}
                  </Button>
                </span>
              ) : null,
          },
        ]}
      />
      {selected ? (
        <GrantSnapshots base="/portal" grantId={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  );
}

interface ReportRow {
  propertyId: string;
  businessDate: string;
  summary: {
    occupancyPct: number | null;
    inHouse: number;
    arrivalsToday: number;
    departuresToday: number;
    roomsInService: number;
  };
}
interface ActivityRow {
  propertyId: string;
  businessDate: string;
  reservationsCreated: number;
  checkIns: number;
  checkOuts: number;
  invoicesIssued: number;
  nightAuditCompleted: boolean;
}
/** day counters projected from synced events (05 §3.2) — anonymised, no guest data */
function Activity() {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['activity'],
    queryFn: () => api.get<ActivityRow[]>('/portal/activity?days=30'),
  });
  const props = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<PropertyCard[]>('/portal/properties'),
  });
  const name = (id: string) => props.data?.find((p) => p.id === id)?.code ?? id.slice(0, 8);
  return (
    <DataGrid<ActivityRow>
      rows={q.data ?? []}
      getRowId={(r) => `${r.propertyId}:${r.businessDate}`}
      emptyText={t('cloud.portal.noData')}
      caption={t('cloud.nav.reports')}
      columns={[
        {
          key: 'd',
          header: t('cloud.portal.businessDate'),
          ltr: true,
          render: (r) => r.businessDate,
        },
        {
          key: 'p',
          header: t('cloud.grants.property'),
          ltr: true,
          render: (r) => name(r.propertyId),
        },
        {
          key: 'res',
          header: t('nav.reservations'),
          ltr: true,
          render: (r) => String(r.reservationsCreated),
        },
        { key: 'ci', header: t('nav.arrivals'), ltr: true, render: (r) => String(r.checkIns) },
        { key: 'co', header: t('nav.departures'), ltr: true, render: (r) => String(r.checkOuts) },
        {
          key: 'inv',
          header: t('billing.invoices'),
          ltr: true,
          render: (r) => String(r.invoicesIssued),
        },
        {
          key: 'na',
          header: t('nav.nightAudit'),
          render: (r) => (
            <Chip tone={r.nightAuditCompleted ? 'success' : 'neutral'}>
              {r.nightAuditCompleted ? t('common.yes') : t('common.no')}
            </Chip>
          ),
        },
      ]}
    />
  );
}

interface SummaryRow {
  propertyId: string;
  code: string;
  nameAr: string;
  roomCap: number;
  daysReported: number;
  avgOccupancyPct: number | null;
  inHouse: number;
  arrivals: number;
  departures: number;
  reservationsCreated: number;
  checkIns: number;
  invoicesIssued: number;
  nightAudits: number;
}
/** P12-05: the owner's properties side by side for the last 30 days */
function Summary() {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['reports', 'summary'],
    queryFn: () =>
      api.get<{
        days: number;
        rows: SummaryRow[];
        totals: Omit<SummaryRow, 'propertyId' | 'code' | 'nameAr' | 'daysReported'> & {
          properties: number;
        };
      }>('/portal/reports/summary?days=30'),
  });
  const pct = (n: number | null) => (n === null ? '—' : `${n}%`);
  // v1.1: the same figures as a WhatsApp message the owner forwards to partners or a family group
  const share = () => {
    const d = q.data;
    if (!d) return;
    const lines = [
      `*${t('cloud.portal.summary', { days: d.days })}*`,
      ...d.rows.map(
        (r) =>
          `${r.nameAr}: ${t('cloud.portal.avgOccupancy')} ${pct(r.avgOccupancyPct)} · ${t('cloud.portal.inHouse')} ${r.inHouse} · ${t('nav.reservations')} ${r.reservationsCreated} · ${t('cloud.portal.nightAudits')} ${r.nightAudits}/${r.daysReported}`,
      ),
      `${t('cloud.portal.avgOccupancy')} ${pct(d.totals.avgOccupancyPct)} · ${t('cloud.portal.inHouse')} ${d.totals.inHouse} · ${t('nav.reservations')} ${d.totals.reservationsCreated}`,
      '— Syrova',
    ];
    window.open(
      `https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`,
      '_blank',
      'noopener',
    );
  };
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">{t('cloud.portal.summary', { days: q.data?.days ?? 30 })}</h2>
        <Button variant="secondary" size="sm" disabled={!q.data} onClick={share}>
          {t('cloud.portal.shareWhatsapp')}
        </Button>
      </div>
      <DataGrid<SummaryRow>
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.propertyId}
        emptyText={t('cloud.portal.noData')}
        caption={t('cloud.portal.summary', { days: 30 })}
        columns={[
          {
            key: 'p',
            header: t('cloud.grants.property'),
            render: (r) => `${r.code} · ${r.nameAr}`,
          },
          {
            key: 'cap',
            header: t('cloud.portal.roomCap'),
            ltr: true,
            render: (r) => String(r.roomCap),
          },
          {
            key: 'occ',
            header: t('cloud.portal.avgOccupancy'),
            ltr: true,
            render: (r) => pct(r.avgOccupancyPct),
          },
          {
            key: 'ih',
            header: t('cloud.portal.inHouse'),
            ltr: true,
            render: (r) => String(r.inHouse),
          },
          {
            key: 'arr',
            header: t('cloud.portal.arrivals'),
            ltr: true,
            render: (r) => String(r.arrivals),
          },
          {
            key: 'res',
            header: t('nav.reservations'),
            ltr: true,
            render: (r) => String(r.reservationsCreated),
          },
          {
            key: 'inv',
            header: t('billing.invoices'),
            ltr: true,
            render: (r) => String(r.invoicesIssued),
          },
          {
            key: 'na',
            header: t('cloud.portal.nightAudits'),
            ltr: true,
            render: (r) => `${r.nightAudits} / ${r.daysReported}`,
          },
        ]}
      />
      {q.data ? (
        <p className="num text-sm">
          {t('cloud.portal.totals')}: {q.data.totals.properties} · {q.data.totals.roomCap} ·{' '}
          {pct(q.data.totals.avgOccupancyPct)} · {q.data.totals.inHouse} · {q.data.totals.arrivals}{' '}
          · {q.data.totals.reservationsCreated} · {q.data.totals.invoicesIssued} ·{' '}
          {q.data.totals.nightAudits}
        </p>
      ) : null}
    </section>
  );
}

function Reports() {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['reports'],
    queryFn: () => api.get<ReportRow[]>('/portal/reports?days=30'),
  });
  const props = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<PropertyCard[]>('/portal/properties'),
  });
  const name = (id: string) => props.data?.find((p) => p.id === id)?.code ?? id.slice(0, 8);
  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={t('cloud.nav.reports')} />
      <p className="text-sm text-text-muted">{t('cloud.portal.reports')}</p>
      <Summary />
      <Activity />
      <DataGrid<ReportRow>
        rows={(q.data ?? []).slice().reverse()}
        getRowId={(r) => `${r.propertyId}:${r.businessDate}`}
        emptyText={t('cloud.portal.noData')}
        caption={t('cloud.nav.reports')}
        columns={[
          {
            key: 'd',
            header: t('cloud.portal.businessDate'),
            ltr: true,
            render: (r) => r.businessDate,
          },
          {
            key: 'p',
            header: t('cloud.grants.property'),
            ltr: true,
            render: (r) => name(r.propertyId),
          },
          {
            key: 'occ',
            header: t('cloud.portal.occupancy'),
            ltr: true,
            render: (r) => `${r.summary.occupancyPct ?? '—'}%`,
          },
          {
            key: 'ih',
            header: t('cloud.portal.inHouse'),
            ltr: true,
            render: (r) => String(r.summary.inHouse),
          },
          {
            key: 'a',
            header: t('cloud.portal.arrivals'),
            ltr: true,
            render: (r) => String(r.summary.arrivalsToday),
          },
          {
            key: 'dep',
            header: t('cloud.portal.departures'),
            ltr: true,
            render: (r) => String(r.summary.departuresToday),
          },
        ]}
      />
    </div>
  );
}

function Audit() {
  const { t, code, action } = useI18n();
  const q = useQuery({
    queryKey: ['audit'],
    queryFn: () =>
      api.get<
        { id: string; occurredAt: string; actorKind: string; action: string; entityType: string }[]
      >('/portal/audit'),
  });
  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={t('cloud.nav.audit')} />
      <ul className="num text-sm text-text-muted">
        {(q.data ?? []).map((a) => (
          <li key={a.id}>
            {fmt(a.occurredAt)} · {code('codes.actorKind', a.actorKind)} · {action(a.action)} ·{' '}
            {code('codes.auditEntity', a.entityType)}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- MFA enrolment gate (09 §4.7, P9-02)
function MfaEnrol({ base, onDone }: { base: '/admin' | '/portal'; onDone: () => void }) {
  const { t } = useI18n();
  const err = (e: unknown) =>
    e instanceof ApiError ? `${e.code}: ${e.message}` : t('errors.generic');
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [copied, setCopied] = useState<'key' | 'codes' | null>(null);
  const copy = (what: 'key' | 'codes', text: string) =>
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(what);
        setTimeout(() => setCopied(null), 2000);
      },
      () => undefined,
    ); // clipboard denied (permissions/insecure context): the text stays selectable
  const start = useMutation({
    mutationFn: () => api.post<{ secret: string; otpauthUri: string }>(`${base}/auth/mfa/setup`),
    onSuccess: (r) => setSetup(r),
  });
  const confirm = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>(`${base}/auth/mfa/confirm`, { code }),
    onSuccess: (r) => setRecovery(r.recoveryCodes),
  });
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-4">
      <section
        className="flex w-full max-w-md flex-col gap-3 rounded-lg border border-border bg-surface p-6"
        aria-labelledby="mfa-title"
      >
        <h1 id="mfa-title" className="text-xl font-semibold">
          {t('cloud.mfa.enrolTitle')}
        </h1>
        <p className="text-sm text-text-muted">{t('cloud.mfa.enrolHint')}</p>
        {recovery ? (
          <>
            <p className="font-semibold">{t('cloud.mfa.recovery')}</p>
            <p className="text-sm text-text-muted">{t('cloud.mfa.recoveryHint')}</p>
            <ul
              className="num grid grid-cols-2 gap-2 rounded-md border border-border bg-bg p-3 font-mono text-sm"
              dir="ltr"
            >
              {recovery.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button variant="secondary" onClick={() => copy('codes', recovery.join('\n'))}>
              {copied === 'codes' ? t('cloud.mfa.copied') : t('cloud.mfa.copyAll')}
            </Button>
            <Button onClick={onDone}>{t('cloud.mfa.continue')}</Button>
          </>
        ) : setup ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              confirm.mutate();
            }}
          >
            <ol className="flex list-decimal flex-col gap-4 ps-5 text-sm marker:font-semibold">
              <li>{t('cloud.mfa.stepApp')}</li>
              <li>
                <div className="flex flex-col gap-3">
                  <span>{t('cloud.mfa.stepScan')}</span>
                  <span className="flex justify-center">
                    <QrCode value={setup.otpauthUri} label={t('cloud.mfa.qrLabel')} />
                  </span>
                  <details>
                    <summary className="cursor-pointer text-text-muted">
                      {t('cloud.mfa.manual')}
                    </summary>
                    <span className="mt-2 flex items-center gap-2">
                      <code
                        className="num flex-1 select-all rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs"
                        dir="ltr"
                        aria-label={t('cloud.mfa.secret')}
                      >
                        {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
                      </code>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => copy('key', setup.secret)}
                      >
                        {copied === 'key' ? t('cloud.mfa.copied') : t('cloud.mfa.copy')}
                      </Button>
                    </span>
                  </details>
                </div>
              </li>
              <li>{t('cloud.mfa.stepCode')}</li>
            </ol>
            <TextField
              label={t('cloud.mfa.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              ltr
              {...(confirm.error ? { error: err(confirm.error) } : {})}
            />
            <Button type="submit" loading={confirm.isPending}>
              {t('cloud.mfa.confirm')}
            </Button>
          </form>
        ) : (
          <Button loading={start.isPending} onClick={() => start.mutate()}>
            {t('cloud.mfa.enrolTitle')}
          </Button>
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------- support session snapshots (ADR-0020)
interface SnapshotRow {
  id: string;
  receivedAt: string;
  snapshot: {
    at: string;
    health: Record<string, unknown>;
    events: { at: string; kind: string; level: string; message: string }[];
    config: Record<string, unknown>;
  };
}
function GrantSnapshots({
  base,
  grantId,
  onClose,
}: {
  base: '/admin' | '/portal';
  grantId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['grant-snapshots', grantId],
    queryFn: () =>
      api.get<{ snapshots: SnapshotRow[] }>(`${base}/support-grants/${grantId}/snapshots`),
    refetchInterval: 30_000,
  });
  const latest = q.data?.snapshots[0];
  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
      aria-label={t('cloud.grants.snapshots')}
    >
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">{t('cloud.grants.snapshots')}</h2>
        <span className="num text-xs text-text-muted">{q.data?.snapshots.length ?? 0}</span>
        <Button size="sm" variant="ghost" className="ms-auto" onClick={onClose}>
          {t('cloud.grants.close')}
        </Button>
      </div>
      {latest ? (
        <>
          <p className="num text-xs text-text-muted">{fmt(latest.receivedAt)}</p>
          <pre className="max-h-64 overflow-auto rounded bg-bg p-2 text-xs" dir="ltr">
            {JSON.stringify(latest.snapshot.health, null, 2)}
          </pre>
          <ul className="num max-h-48 overflow-auto text-xs" dir="ltr">
            {latest.snapshot.events.map((e, i) => (
              <li key={i}>
                {e.at.slice(0, 19).replace('T', ' ')} · {e.level} · {e.kind} · {e.message}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-text-muted">{t('cloud.grants.noSnapshots')}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- tickets (P10-07)
interface TicketRow {
  id: string;
  tenantCode: string;
  propertyCode: string | null;
  propertyNameAr: string | null;
  title: string;
  severity: string;
  status: string;
  updatedAt: string;
}
interface TicketDetail extends TicketRow {
  body: string;
  comments: { id: string; authorKind: string; body: string; createdAt: string }[];
}
const SEV_TONE: Record<string, string> = {
  low: 'neutral',
  normal: 'info',
  high: 'warning',
  critical: 'danger',
};
const ST_TONE: Record<string, string> = {
  open: 'warning',
  in_progress: 'info',
  waiting_hotel: 'warning',
  resolved: 'success',
  closed: 'neutral',
};
function Tickets({
  base,
  canManage,
  propertyOptions,
}: {
  base: '/admin' | '/portal';
  canManage: boolean;
  propertyOptions: { value: string; label: string }[];
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const err = (e: unknown) =>
    e instanceof ApiError ? `${e.code}: ${e.message}` : t('errors.generic');
  const [all, setAll] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [f, setF] = useState({ propertyId: '', title: '', body: '', severity: 'normal' });
  const [reply, setReply] = useState('');
  const q = useQuery({
    queryKey: ['tickets', base, all],
    queryFn: () => api.get<TicketRow[]>(`${base}/tickets?status=${all ? 'all' : 'open'}`),
    refetchInterval: 30_000,
  });
  const detail = useQuery({
    queryKey: ['ticket', base, selected],
    enabled: Boolean(selected),
    queryFn: () => api.get<TicketDetail>(`${base}/tickets/${selected}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tickets'] });
    void qc.invalidateQueries({ queryKey: ['ticket'] });
  };
  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>(`${base}/tickets`, {
        ...(f.propertyId ? { propertyId: f.propertyId } : {}),
        title: f.title,
        body: f.body,
        severity: f.severity,
      }),
    onSuccess: (r) => {
      toast.toast('success', t('cloud.tickets.created'));
      setF({ propertyId: '', title: '', body: '', severity: 'normal' });
      setSelected(r.id);
      refresh();
    },
    onError: (e) => toast.toast('error', err(e)),
  });
  const update = useMutation({
    mutationFn: (body: { status?: string; assignedTo?: string | null }) =>
      api.put(`${base}/tickets/${selected}`, body),
    onSuccess: refresh,
    onError: (e) => toast.toast('error', err(e)),
  });
  const comment = useMutation({
    mutationFn: () => api.post(`${base}/tickets/${selected}/comments`, { body: reply }),
    onSuccess: () => {
      setReply('');
      refresh();
    },
    onError: (e) => toast.toast('error', err(e)),
  });
  const d = detail.data;
  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={t('cloud.tickets.title')} />
      {base === '/portal' ? (
        <p className="text-sm text-text-muted">{t('cloud.tickets.portalHint')}</p>
      ) : null}
      <form
        className="grid grid-cols-1 items-end gap-2 md:grid-cols-5"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <SelectField
          label={t('cloud.tickets.property')}
          value={f.propertyId}
          onChange={(e) => setF({ ...f, propertyId: e.target.value })}
          options={[{ value: '', label: '—' }, ...propertyOptions]}
        />
        <TextField
          label={t('cloud.tickets.subject')}
          value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
          required
        />
        <TextField
          label={t('cloud.tickets.body')}
          value={f.body}
          onChange={(e) => setF({ ...f, body: e.target.value })}
          required
        />
        <SelectField
          label={t('cloud.tickets.severity')}
          value={f.severity}
          onChange={(e) => setF({ ...f, severity: e.target.value })}
          options={['low', 'normal', 'high', 'critical'].map((s) => ({
            value: s,
            label: t(`cloud.tickets.severities.${s}` as 'cloud.tickets.severities.low'),
          }))}
        />
        <Button type="submit" loading={create.isPending}>
          {t('cloud.tickets.new')}
        </Button>
      </form>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
        {t('cloud.tickets.showAll')}
      </label>
      <DataGrid<TicketRow>
        rows={q.data ?? []}
        getRowId={(r) => r.id}
        emptyText={t('cloud.tickets.empty')}
        caption={t('cloud.tickets.title')}
        columns={[
          {
            key: 'when',
            header: t('cloud.tickets.updated'),
            ltr: true,
            render: (r) => fmt(r.updatedAt),
          },
          {
            key: 'prop',
            header: t('cloud.tickets.property'),
            render: (r) =>
              r.propertyCode
                ? `${r.tenantCode} / ${r.propertyCode} · ${r.propertyNameAr}`
                : r.tenantCode,
          },
          {
            key: 'title',
            header: t('cloud.tickets.subject'),
            render: (r) => (
              <button
                type="button"
                className="text-start underline"
                onClick={() => setSelected(r.id)}
              >
                {r.title}
              </button>
            ),
          },
          {
            key: 'sev',
            header: t('cloud.tickets.severity'),
            render: (r) => (
              <Chip tone={SEV_TONE[r.severity] ?? 'neutral'}>
                {t(`cloud.tickets.severities.${r.severity}` as 'cloud.tickets.severities.low')}
              </Chip>
            ),
          },
          {
            key: 'st',
            header: t('cloud.tickets.status'),
            render: (r) => (
              <Chip tone={ST_TONE[r.status] ?? 'neutral'}>
                {t(`cloud.tickets.statuses.${r.status}` as 'cloud.tickets.statuses.open')}
              </Chip>
            ),
          },
        ]}
      />
      {selected && d ? (
        <section
          className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
          aria-label={d.title}
        >
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{d.title}</h2>
            <Chip tone={ST_TONE[d.status] ?? 'neutral'}>
              {t(`cloud.tickets.statuses.${d.status}` as 'cloud.tickets.statuses.open')}
            </Chip>
            <Button size="sm" variant="ghost" className="ms-auto" onClick={() => setSelected(null)}>
              {t('cloud.grants.close')}
            </Button>
          </div>
          <p className="whitespace-pre-wrap text-sm">{d.body}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {d.comments.map((c) => (
              <li
                key={c.id}
                className={`rounded px-2 py-1 ${c.authorKind === 'staff' ? 'bg-info/10' : 'bg-surface-muted'}`}
              >
                <span className="num text-xs text-text-muted">
                  {fmt(c.createdAt)} · {c.authorKind}
                </span>
                <p className="whitespace-pre-wrap">{c.body}</p>
              </li>
            ))}
          </ul>
          <form
            className="flex items-end gap-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              comment.mutate();
            }}
          >
            <TextField
              label={t('cloud.tickets.comment')}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              required
              className="flex-1"
            />
            <Button type="submit" size="sm" loading={comment.isPending}>
              {t('cloud.tickets.send')}
            </Button>
          </form>
          <div className="flex flex-wrap gap-1">
            {(canManage ? ['in_progress', 'waiting_hotel', 'resolved', 'closed'] : ['closed']).map(
              (s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="secondary"
                  onClick={() => update.mutate({ status: s })}
                >
                  {t(`cloud.tickets.statuses.${s}` as 'cloud.tickets.statuses.open')}
                </Button>
              ),
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- channel bookings (ADR-0022)
interface ChannelBookingRow {
  id: string;
  status: string;
  mode: 'instant' | 'requested';
  cloudConfirmation: string;
  hotelConfirmation: string | null;
  propertyId: string;
  propertyCode: string;
  channelCode: string;
  roomType: string | null;
  arrival: string;
  departure: string;
  guest: { name?: string; phone?: string };
  requestedUntil: string | null;
  appliedAt: string | null;
}
function ChannelBookings() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const err = (e: unknown) =>
    e instanceof ApiError ? `${e.code}: ${e.message}` : t('errors.generic');
  const [filter, setFilter] = useState<'requested' | 'all'>('requested');
  const q = useQuery({
    queryKey: ['channel-bookings', filter],
    queryFn: () =>
      api.get<ChannelBookingRow[]>(
        `/portal/channel-bookings${filter === 'requested' ? '?status=requested' : ''}`,
      ),
    refetchInterval: 20_000,
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'accept' | 'reject' }) =>
      api.post(`/portal/channel-bookings/${id}/decide`, { decision }),
    onSuccess: () => {
      toast.toast('success', t('cloud.channels.pendingNode'));
      void qc.invalidateQueries({ queryKey: ['channel-bookings'] });
    },
    onError: (e) => toast.toast('error', err(e)),
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      api.post(`/portal/channel-bookings/${id}/cancel`, { reason: 'hotel' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['channel-bookings'] }),
    onError: (e) => toast.toast('error', err(e)),
  });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">{t('cloud.channels.bookingsTitle')}</h1>
        <Button
          size="sm"
          variant={filter === 'requested' ? 'primary' : 'ghost'}
          onClick={() => setFilter('requested')}
        >
          {t('cloud.channels.statuses.requested')}
        </Button>
        <Button
          size="sm"
          variant={filter === 'all' ? 'primary' : 'ghost'}
          onClick={() => setFilter('all')}
        >
          {t('cloud.channels.all')}
        </Button>
      </div>
      <DataGrid<ChannelBookingRow>
        rows={q.data ?? []}
        getRowId={(r) => r.id}
        emptyText={t('common.noResults')}
        caption={t('cloud.channels.bookingsTitle')}
        columns={[
          {
            key: 'c',
            header: t('cloud.channels.code'),
            ltr: true,
            render: (r) => `${r.cloudConfirmation} · ${r.channelCode}`,
          },
          {
            key: 'p',
            header: t('cloud.grants.property'),
            ltr: true,
            render: (r) => `${r.propertyCode} ${r.roomType ?? ''}`,
          },
          {
            key: 'd',
            header: t('reservation.arrival'),
            ltr: true,
            render: (r) => `${r.arrival} → ${r.departure}`,
          },
          {
            key: 'g',
            header: t('nav.guests'),
            render: (r) => `${r.guest.name ?? ''} ${r.guest.phone ?? ''}`,
          },
          {
            key: 's',
            header: t('cloud.channels.status'),
            render: (r) => (
              <Chip
                tone={
                  ['confirmed', 'accepted'].includes(r.status)
                    ? 'success'
                    : r.status === 'requested'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {t(`cloud.channels.statuses.${r.status}` as 'cloud.channels.statuses.held')}
                {r.requestedUntil && r.status === 'requested'
                  ? ` · ${r.requestedUntil.slice(11, 16)}`
                  : ''}
              </Chip>
            ),
          },
          {
            key: 'h',
            header: t('cloud.channels.hotelRef'),
            ltr: true,
            render: (r) => r.hotelConfirmation ?? (r.appliedAt ? t('cloud.channels.applied') : '—'),
          },
          {
            key: 'act',
            header: t('common.actions'),
            render: (r) =>
              r.status === 'requested' ? (
                <span className="flex gap-1">
                  <Button
                    size="sm"
                    loading={decide.isPending}
                    onClick={() => decide.mutate({ id: r.id, decision: 'accept' })}
                  >
                    {t('cloud.channels.decideAccept')}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={decide.isPending}
                    onClick={() => decide.mutate({ id: r.id, decision: 'reject' })}
                  >
                    {t('cloud.channels.decideReject')}
                  </Button>
                </span>
              ) : ['confirmed', 'accepted'].includes(r.status) ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={cancel.isPending}
                  onClick={() => cancel.mutate(r.id)}
                >
                  {t('cloud.channels.cancel')}
                </Button>
              ) : null,
          },
        ]}
      />
    </div>
  );
}

function PortalTickets() {
  const props = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<PropertyCard[]>('/portal/properties'),
  });
  return (
    <Tickets
      base="/portal"
      canManage={false}
      propertyOptions={(props.data ?? []).map((p) => ({
        value: p.id,
        label: `${p.code} · ${p.nameAr}`,
      }))}
    />
  );
}

function Shell({ me }: { me: Owner }) {
  const { t, locale, setLocale } = useI18n();
  const qc = useQueryClient();
  const logout = useMutation({
    mutationFn: () => api.post<void>('/portal/auth/logout'),
    onSettled: () => qc.setQueryData(['me'], null),
  });
  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <aside
        className="flex w-56 shrink-0 flex-col border-e border-border bg-surface"
        aria-label={t('cloud.portalName')}
      >
        <SidebarBrand name={t('app.name')} sub={t('cloud.portalSub')} />
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-0.5">
            <li>
              <NavLink to="/properties" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="building" size={18} />
                <span className="truncate">{t('cloud.nav.properties')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/grants" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="lock" size={18} />
                <span className="truncate">{t('cloud.nav.grants')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/reports" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="reports" size={18} />
                <span className="truncate">{t('cloud.nav.reports')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/channel-bookings" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="channels" size={18} />
                <span className="truncate">{t('cloud.channels.nav')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/tickets" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="ticket" size={18} />
                <span className="truncate">{t('cloud.tickets.portalNav')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/audit" className={({ isActive }) => navItemClass(isActive)}>
                <Icon name="auditLog" size={18} />
                <span className="truncate">{t('cloud.nav.audit')}</span>
              </NavLink>
            </li>
          </ul>
        </nav>
        <div className="flex shrink-0 flex-col gap-2 border-t border-border p-3 text-xs">
          <span className="flex items-center gap-2 text-text-muted">
            <Icon name="user" size={16} />
            <span className="truncate">{me.displayName}</span>
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              icon="language"
              className="h-7"
              onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
            >
              {locale === 'ar' ? 'English' : 'العربية'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon="logout"
              className="ms-auto h-7"
              onClick={() => logout.mutate()}
            >
              {t('cloud.logout')}
            </Button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-auto p-5">
        <Routes>
          <Route path="/properties" element={<Properties />} />
          <Route path="/grants" element={<Grants me={me} />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/tickets" element={<PortalTickets />} />
          <Route path="/channel-bookings" element={<ChannelBookings />} />
          <Route path="*" element={<Navigate to="/properties" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function App() {
  const { t } = useI18n();
  const me = useMe();
  return (
    <BrowserRouter>
      {me.isPending ? (
        <p className="p-6 text-text-muted">{t('common.loading')}</p>
      ) : me.data && me.data.role === 'tenant_admin' && !me.data.mfaEnrolled ? (
        <MfaEnrol base="/portal" onDone={() => void me.refetch()} />
      ) : me.data ? (
        <Shell me={me.data} />
      ) : (
        <Login />
      )}
    </BrowserRouter>
  );
}
